import type { ShiftTemplate } from "@station/contracts";
import { Alert, Button, Card, Checkbox, EmptyState, SkeletonRows, StatusBadge } from "@station/ui";
import { createFileRoute } from "@tanstack/react-router";
import { CalendarClock, Plus } from "lucide-react";
import { type ReactElement, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  SimpleTable,
  tdClass,
  tdRightClass,
  thClass,
  thRightClass,
} from "../../../features/counterparties/shared.js";
import { CoverageStrip } from "../../../features/shifts/coverage-strip.js";
import { useShiftPermissions, useShiftTemplates } from "../../../features/shifts/queries.js";
import {
  NoStations,
  ShiftStationField,
  ShiftsPage,
  shiftsSearchShape,
  useShiftStation,
} from "../../../features/shifts/shared.js";
import { TemplateDialog } from "../../../features/shifts/template-dialog.js";
import {
  crossesMidnight,
  formatDurationText,
  sortTemplates,
} from "../../../features/shifts/template-values.js";
import { errorMessage } from "../../../lib/errors.js";

const searchSchema = z.object({ ...shiftsSearchShape });

export const Route = createFileRoute("/_app/vardiya/sablonlar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: TemplatesPage,
});

/**
 * Vardiya şablonları + istasyonun **24 saat kapsama şeridi**. Boşluk ve çakışma BİLGİLENDİRMEDİR:
 * 24 saat açık olmayan istasyonda boşluk olağandır, ama çakışma iki vardiyanın aynı anda planlandığını söyler.
 */
function TemplatesPage(): ReactElement {
  const { t } = useTranslation();
  const permissions = useShiftPermissions();
  return (
    <ShiftsPage
      title={t("menu.shiftTemplates")}
      description={t("shifts.templates.description")}
      breadcrumb={[{ label: t("menu.shiftTemplates") }]}
    >
      {({ companyId }) =>
        permissions.canRead ? (
          <TemplatesBody companyId={companyId} />
        ) : (
          <Alert tone="info">{t("shifts.noPermission")}</Alert>
        )
      }
    </ShiftsPage>
  );
}

function TemplatesBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();
  const permissions = useShiftPermissions();
  const station = useShiftStation(companyId);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<{ template: ShiftTemplate | null } | null>(null);
  const templates = useShiftTemplates(companyId, station.stationId, includeInactive);

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stationId === null) return <NoStations />;

  const items = sortTemplates(templates.data?.items ?? []);
  const coverage = (templates.data?.coverage ?? []).find((row) => row.stationId === station.stationId);

  return (
    <>
      <Card className="flex flex-wrap items-end justify-between gap-3 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <ShiftStationField selection={station} />
          <label className="flex min-h-11 items-center gap-2 text-sm">
            <Checkbox
              className="size-5"
              checked={includeInactive}
              onChange={(event) => setIncludeInactive(event.target.checked)}
            />
            {t("shifts.templates.includeInactive")}
          </label>
        </div>
        {permissions.canManage ? (
          <Button onClick={() => setEditing({ template: null })}>
            <Plus aria-hidden="true" />
            {t("shifts.templates.newTitle")}
          </Button>
        ) : null}
      </Card>

      <Card className="grid gap-3 p-4" aria-labelledby={headingId}>
        <h2 id={headingId} className="text-base font-semibold">
          {t("shifts.templates.coverage.label")}
        </h2>
        <CoverageStrip coverage={coverage} />
      </Card>

      {templates.error ? (
        <Alert tone="danger">{errorMessage(templates.error)}</Alert>
      ) : templates.isPending ? (
        <SkeletonRows rows={4} />
      ) : items.length === 0 ? (
        <EmptyState
          headingLevel={2}
          icon={<CalendarClock />}
          title={t("shifts.templates.emptyTitle")}
          description={t("shifts.templates.emptyDescription")}
          action={
            permissions.canManage ? (
              <Button onClick={() => setEditing({ template: null })}>
                <Plus aria-hidden="true" />
                {t("shifts.templates.newTitle")}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <SimpleTable
          caption={t("shifts.templates.caption")}
          head={
            <tr>
              <th scope="col" className={thRightClass}>
                {t("shifts.templates.fields.sortOrder")}
              </th>
              <th scope="col" className={thClass}>
                {t("shifts.templates.fields.name")}
              </th>
              <th scope="col" className={thClass}>
                {t("shifts.templates.fields.window")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("shifts.templates.fields.duration")}
              </th>
              <th scope="col" className={thClass}>
                {t("shifts.templates.fields.businessDayOffset")}
              </th>
              <th scope="col" className={thClass}>
                {t("common.status")}
              </th>
              <th scope="col" className={thClass}>
                {t("common.actions")}
              </th>
            </tr>
          }
        >
          {items.map((template) => (
            <tr key={template.id} className="border-t align-top">
              <td className={tdRightClass}>{template.sortOrder}</td>
              <td className={tdClass}>{template.name}</td>
              <td className={`${tdClass} tabular`}>
                {template.startTime}–{template.endTime}
                {crossesMidnight(template.startTime, template.endTime) ? (
                  <>
                    {" "}
                    <StatusBadge tone="info">{t("shifts.templates.endsNextDay")}</StatusBadge>
                  </>
                ) : null}
              </td>
              <td className={tdRightClass}>{formatDurationText(template.durationMinutes)}</td>
              <td className={tdClass}>
                {t(
                  `shifts.templates.offset.${
                    template.businessDayOffset === -1
                      ? "previous"
                      : template.businessDayOffset === 1
                        ? "next"
                        : "same"
                  }`,
                )}
              </td>
              <td className={tdClass}>
                <StatusBadge tone={template.isActive ? "success" : "neutral"}>
                  {template.isActive ? t("common.active") : t("common.inactive")}
                </StatusBadge>
              </td>
              <td className={tdClass}>
                {permissions.canManage ? (
                  <Button variant="outline" size="sm" onClick={() => setEditing({ template })}>
                    {t("common.edit")}
                  </Button>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>
            </tr>
          ))}
        </SimpleTable>
      )}

      <TemplateDialog
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
        companyId={companyId}
        stations={station.stations}
        defaultStationId={station.stationId}
        template={editing?.template ?? null}
      />
    </>
  );
}
